// Single source of truth for WhatsApp ordering.
// Indian retail shops typically share a single business WhatsApp number.
export const WHATSAPP_NUMBER = "919844013145"; // +91 98440 13145
export const SHOP_NAME = "Adeshwar Electricals";

/** Build a wa.me URL with a prefilled message. */
export function buildProductWhatsAppUrl(product, qty = 1) {
  const priceText = product.price
    ? `*₹${product.price.toLocaleString("en-IN")}*`
    : "";
  const lines = [
    `Hi ${SHOP_NAME}, I'd like to order:`,
    "",
    `*${product.name}*`,
    product.brand ? `Brand: ${product.brand}` : null,
    `Quantity: ${qty}`,
    priceText && `Price: ${priceText}`,
    "",
    "Please confirm availability and delivery time. Thanks!",
  ].filter(Boolean);
  const message = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
}

/** Build a wa.me URL for the whole cart. */
export function buildCartWhatsAppUrl(items, total) {
  if (!items || items.length === 0) {
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      `Hi ${SHOP_NAME}, I'd like to enquire about a bulk order.`
    )}`;
  }
  const lines = [
    `Hi ${SHOP_NAME}, I'd like to place this order:`,
    "",
    ...items.map(
      (i, idx) =>
        `${idx + 1}. ${i.name} × ${i.qty} — ₹${(
          i.price * i.qty
        ).toLocaleString("en-IN")}`
    ),
    "",
    `*Total: ₹${(total || 0).toLocaleString("en-IN")}*`,
    "",
    "Please confirm and share delivery time. Thanks!",
  ];
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    lines.join("\n")
  )}`;
}

/** Generic enquiry. */
export function buildGeneralWhatsAppUrl() {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hi ${SHOP_NAME}, I'd like to enquire about your products.`
  )}`;
}

export const GST_RATE = 0.18;

/** GST is EXTRA on top of the (discounted) price. base = price, gst = 18% added on top. */
export function gstBreakup(amount) {
  const base = Math.round(amount * 100) / 100;
  const gst = Math.round(base * GST_RATE * 100) / 100;
  return { base, gst };
}

/** Round to 2 decimals (money). */
export const r2 = (n) => Math.round(n * 100) / 100;

/** WhatsApp enquiry for an out-of-stock product. */
export function buildStockEnquiryUrl(product) {
  const lines = [
    `Hi ${SHOP_NAME},`,
    "",
    `*${product.name}*${product.brand ? ` (${product.brand})` : ""} is showing "No stock — coming soon".`,
    "When will it be back in stock? Please share details. Thanks!",
  ];
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
}

/** Plain-text bill for sharing. */
export function buildBillText(items, { baseSubtotal, gst, includeGst, deliveryFee, productsTotal, total }) {
  const lines = [
    `*${SHOP_NAME} — Bill*`,
    "",
    ...items.map((i, idx) => {
      const sel = i.variantInfo?.selections
        ? ` (${Object.values(i.variantInfo.selections).join(" / ")})`
        : "";
      return `${idx + 1}. ${i.name}${sel} × ${i.qty} — ₹${(i.price * i.qty).toLocaleString("en-IN")}`;
    }),
    "",
    `Sub Total (excl. GST): ₹${baseSubtotal.toLocaleString("en-IN")}`,
    includeGst ? `GST (18%): ₹${gst.toLocaleString("en-IN")}` : `GST: Not applied`,
    `Products total (without delivery): ₹${productsTotal.toLocaleString("en-IN")}`,
    `Delivery: ${deliveryFee === 0 ? "Not applied" : `₹${deliveryFee}`}`,
    `*Grand Total: ₹${total.toLocaleString("en-IN")}*`,
    "",
    "30/1 M.T. Street, B.V.K. Iyengar Road Cross, Chickpet, Bangalore – 560053",
    "Ph: 080-41249285",
  ];
  return lines.join("\n");
}

/** wa.me URL to send the bill to any mobile number. */
export function buildBillShareUrl(phone, billText) {
  let digits = (phone || "").replace(/\D/g, "").replace(/^0+/, "");
  if (digits.length === 10) digits = `91${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(billText)}`;
}
